import { Checkbox } from "@certa/catalyst/components/Checkbox";
import { classNames } from "@certa/catalyst/utils/common";
import { ArrowDownLight } from "@certa/icons/components/ArrowDownLight";
import { ArrowUpLight } from "@certa/icons/components/ArrowUpLight";
import { SortLight } from "@certa/icons/components/SortLight";

import { FilterOverlay } from "../TableFilters/FilterOverlay/FilterOverlay";
import {
  InternalTableHeaderResizer,
  type InternalTableHeaderResizerProps
} from "./InternalTableHeaderResizer/InternalTableHeaderResizer";
import { InternalTableHeaderPinner } from "./InternalTableHeaderPinner";

import { useInternalTableDndSortable } from "../InternalTableDnd/hooks/useInternalTableDndSortable";

import { getCommonPinningStyles } from "./InternalTableHeaderPinner/InternalTableHeaderPinner.utils";
import { CSS } from "../InternalTableDnd/InternalTableDnd.utils";

import { flexRender } from "../utils/render";

import { EXPAND_ROW_COLUMN_ID, SELECT_ROW_COLUMN_ID } from "../Table.constants";

import type { Header, SortDirection } from "../Table.types";

import styles from "./InternalTableHeader.module.css";

type InternalTableHeaderProps<TableData> = {
  header: Header<TableData, unknown>;
  canEnableColumnOrdering: boolean;
  onToggleColumnPin?: (columnId: string, isPinned: boolean) => void;
  onRowSelect?: (rowIds: string[]) => void;
  canEnableMultiRowSelection?: boolean;
  canEnableAllRowsSelection?: boolean;
  isManualFiltering: boolean;
} & InternalTableHeaderResizerProps<TableData>;

export const InternalTableHeader = <TableData extends Record<string, any>>(
  props: InternalTableHeaderProps<TableData>
) => {
  const {
    header,
    columnResizeDirection,
    columnResizeMode,
    columnSizingInfo,
    canEnableColumnOrdering,
    onToggleColumnPin,
    onRowSelect,
    canEnableMultiRowSelection = true,
    canEnableAllRowsSelection = true,
    isManualFiltering
  } = props;

  const canSort = header.column.getCanSort();
  const canFilter = header.column.getCanFilter();

  const handleSearchFilter = (value: any) => {
    header.column.setFilterValue(value);
    header.column.columnDef.meta?.onFilter?.(value);
  };

  const handleResetFilter = () => {
    header.column.setFilterValue(undefined);
    header.column.columnDef.meta?.onFilter?.(undefined);
  };

  const getSortButtonLabel = () => {
    const columnName = header.column.columnDef.header?.toString() || "column";
    const sortState = header.column.getIsSorted();

    if (sortState === false) return `Sort ${columnName} in ascending order`;
    if (sortState === "asc") return `Sort ${columnName} in descending order`;
    return `Remove sorting from ${columnName}`;
  };

  const renderSortIcon = () => {
    const sortState = header.column.getIsSorted();

    if (sortState === false) return <SortLight />;
    if (sortState === "asc") return <ArrowUpLight />;
    return <ArrowDownLight />;
  };

  const handleSortKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleOnSort();
    }
  };

  const handleOnSort = () => {
    /*
      `toggleSorting` takes in the desc order as parameter. So Passing
      the next sorting order to the column to update the next sorting state.
    */
    header.column.toggleSorting(
      header.column.getNextSortingOrder()
        ? header.column.getNextSortingOrder() === "desc"
        : undefined
    );
    /*
      Why using `getNextSortingOrder`?
      Reason: The column sorting order is updated after the `toggleSorting`
      function is called. So, to get the current sorting order, we are using
      `getNextSortingOrder` function.
    */
    const sortOrder = header.column.getNextSortingOrder();
    const currentSortOrder: SortDirection | undefined =
      typeof sortOrder === "boolean" ? undefined : sortOrder;
    /*
      Calling the onSort callback from the column definition meta
      with current sorting order
    */
    header.column.columnDef.meta?.onSort?.(currentSortOrder);
  };

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useInternalTableDndSortable(header.column);

  const columnPinningStyles = getCommonPinningStyles<TableData>(header.column);

  const canShowRowSelectionColumn =
    header.column.columnDef.id === SELECT_ROW_COLUMN_ID;

  const canShowRowExpandColumn =
    header.column.columnDef.id === EXPAND_ROW_COLUMN_ID;

  const tableInstance = header.getContext().table;

  if (canShowRowExpandColumn) {
    return (
      <th
        scope="col"
        key={header.id}
        className={classNames({
          [styles.tableColumn]: true,
          [styles.tableColumnRowExpand]: true
        })}
        style={{
          ...columnPinningStyles,
          width: header.column.getSize(),
          minWidth: header.column.getSize(),
          maxWidth: header.column.getSize(),
          boxShadow: "none"
        }}
      >
        <span className="catalyst-screen-reader-only">Expand all rows</span>
      </th>
    );
  }

  if (canShowRowSelectionColumn && canEnableAllRowsSelection) {
    const currentRows = tableInstance
      .getCoreRowModel()
      .rows.filter(row => row.getCanSelect());
    const canSelectAnyRow = currentRows.length > 0;

    const handleRowSelect = (
      isAllRowChecked: boolean,
      evt: React.ChangeEvent<HTMLInputElement>
    ) => {
      tableInstance.getToggleAllRowsSelectedHandler()(evt);
      // Getting the current rows from the table instance because the rows in the header
      // are not updated yet when the select all checkbox is clicked
      if (isAllRowChecked) {
        onRowSelect?.(currentRows.map(row => row.id));
      } else {
        onRowSelect?.([]);
      }
    };

    const isAllRowsSelected = tableInstance.getIsAllRowsSelected();
    const isSomeRowsSelected = tableInstance.getIsSomeRowsSelected();

    return (
      <th
        scope="col"
        key={header.id}
        className={classNames({
          [styles.tableColumn]: true,
          [styles.tableColumnRowSelect]: true
        })}
        style={{
          ...columnPinningStyles,
          //https://github.com/TanStack/table/discussions/4707#discussion-4853295
          width: header.column.getSize(),
          boxShadow: "none"
        }}
      >
        {canEnableMultiRowSelection ? (
          <Checkbox
            name="Select all rows"
            aria-label="Select all rows"
            checked={isAllRowsSelected}
            indeterminate={isSomeRowsSelected && !isAllRowsSelected}
            onChange={handleRowSelect}
            disabled={!canSelectAnyRow}
          />
        ) : null}
      </th>
    );
  }

  const columnOrderingProps = canEnableColumnOrdering
    ? {
        ...attributes,
        onMouseDown: (evt: MouseEvent) => {
          // Only trigger drag if clicking directly on the header element,
          // not on child elements like sort icons or filter buttons
          if (evt.target === evt.currentTarget) {
            listeners?.onMouseDown(evt);
          }
        },
        onTouchStart: (evt: TouchEvent) => {
          if (evt.target === evt.currentTarget) {
            listeners?.onTouchStart(evt);
          }
        },
        onKeyDown: (evt: TouchEvent) => {
          if (evt.target === evt.currentTarget) {
            listeners?.onKeyDown(evt);
          }
        }
      }
    : {};

  return (
    <th
      title={header.column.columnDef.header?.toString()}
      scope="col"
      key={header.id}
      colSpan={header.colSpan}
      ref={setNodeRef}
      className={styles.tableColumn}
      style={{
        opacity: isDragging ? 0.8 : 1,
        position: "relative",
        // translate instead of transform to avoid squishing
        transform: CSS.Translate.toString(transform),
        transition: transition ?? "",
        whiteSpace: "nowrap",
        width: header.column.getSize(),
        ...columnPinningStyles,
        zIndex: isDragging ? 1 : columnPinningStyles.zIndex
      }}
    >
      <div
        className={classNames({
          [styles.tableHeader]: true,
          [styles.tableHeaderCanReorder]: canEnableColumnOrdering,
          [styles.tableHeaderDragging]: isDragging
        })}
        data-testid={`column-reorder-${header.column.id}`}
        {...columnOrderingProps}
      >
        <div className={styles.tableHeaderContent}>
          {flexRender(header.column.columnDef.header, header.getContext())}
        </div>
        {canSort && (
          <button
            className={classNames({
              [styles.tableSortIcon]: true,
              [styles.tableSortIconActive]:
                header.column.getIsSorted() !== false
            })}
            onClick={handleOnSort}
            onKeyDown={handleSortKeyDown}
            type="button"
            aria-label={getSortButtonLabel()}
            title={getSortButtonLabel()}
          >
            {renderSortIcon()}
          </button>
        )}
        <div className={styles.tableHeaderActions}>
          {canFilter && (
            <FilterOverlay
              column={header.column}
              onSearch={handleSearchFilter}
              onReset={handleResetFilter}
              isManualFiltering={isManualFiltering}
            />
          )}
          {/* {canEnableColumnOrdering && (
            <button
              {...attributes}
              {...listeners}
              className={styles.tableColumnReorderIcon}
              data-testid={`column-reorder-${header.column.id}`}
            >
              <Reorder size={12} />
            </button>
          )} */}
          <InternalTableHeaderPinner
            header={header}
            onToggleColumnPin={onToggleColumnPin}
          />
        </div>
      </div>
      {!isDragging && (
        <InternalTableHeaderResizer
          header={header}
          columnResizeDirection={columnResizeDirection}
          columnResizeMode={columnResizeMode}
          columnSizingInfo={columnSizingInfo}
        />
      )}
    </th>
  );
};
